import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import apiClient from "../core/config/api";
import { FREEZE_FOR_TESTING } from "./modalTestMode";
import "./ResolveCatchAllModal.css";
import "./TopUpCreditsModal.css";

// Fallback only, shown until /stripe-payment/topup-options answers. The
// backend's tier list is the real price list and the only one checkout charges.
export const TOP_UP_TIERS: { credits: number; label: string; price: number }[] = [
    { credits: 1_000, label: "1k", price: 9 },
    { credits: 5_000, label: "5k", price: 15 },
    { credits: 10_000, label: "10k", price: 29 },
    { credits: 25_000, label: "25K", price: 49 },
    { credits: 50_000, label: "50K", price: 79 },
    { credits: 100_000, label: "100K", price: 139 },
    { credits: 500_000, label: "500K", price: 259 },
    { credits: 1_000_000, label: "1M", price: 429 },
    { credits: 2_000_000, label: "2M", price: 749 },
];
const DEFAULT_TIER_INDEX = 2;

// Backend: tiers + eligibility, and the one-time Stripe checkout for a top-up.
const TOP_UP_OPTIONS_ENDPOINT = "/stripe-payment/topup-options";
const TOP_UP_CHECKOUT_ENDPOINT = "/stripe-payment/create-topup-checkout-session";

interface TopUpCreditsModalProps {
    onClose: () => void;
}

// Indian digit grouping, as in the design: 1,00,000 / 10,00,000.
const formatNumber = (value: number) => value.toLocaleString("en-IN");
const formatPrice = (value: number) =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
        maximumFractionDigits: 2,
    }).format(value);
const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const TopUpCreditsModal = ({ onClose }: TopUpCreditsModalProps) => {
    const [tiers, setTiers] = useState(TOP_UP_TIERS);
    const [tierIndex, setTierIndex] = useState(DEFAULT_TIER_INDEX);
    const [renewalDate, setRenewalDate] = useState<string | null>(null);
    // Free users keep top-ups 12 months; paid subscribers until the plan renews.
    const [twelveMonthValidity, setTwelveMonthValidity] = useState(false);
    const [blockedReason, setBlockedReason] = useState<string | null>(null);
    const [optionsLoading, setOptionsLoading] = useState(!FREEZE_FOR_TESTING);
    const [submitting, setSubmitting] = useState(false);

    const tier = tiers[Math.min(tierIndex, tiers.length - 1)];
    const perCredit = tier.price / tier.credits;
    const lastIndex = tiers.length - 1;

    useEffect(() => {
        if (FREEZE_FOR_TESTING) {
            setRenewalDate("2026-10-01T00:00:00");
            return;
        }
        let cancelled = false;
        apiClient
            .get(TOP_UP_OPTIONS_ENDPOINT)
            .then(({ data }) => {
                if (cancelled) return;
                const loaded = Array.isArray(data?.tiers) && data.tiers.length ? data.tiers : null;
                if (loaded) {
                    setTiers(loaded);
                    const preferred = loaded.findIndex((t: { credits: number }) => t.credits === data?.default_credits);
                    setTierIndex(preferred >= 0 ? preferred : Math.min(DEFAULT_TIER_INDEX, loaded.length - 1));
                }
                if (data?.expires_at) setRenewalDate(data.expires_at);
                setTwelveMonthValidity(data?.validity === "12_months");
                setBlockedReason(data?.can_purchase === false ? data?.reason || "Top-ups are not available for this account." : null);
            })
            .catch(() => {
                // Non-fatal - keep the fallback tiers; checkout still validates.
            })
            .finally(() => {
                if (!cancelled) setOptionsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const close = () => {
        if (!submitting) onClose();
    };

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !submitting) onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [submitting, onClose]);

    const buy = async () => {
        if (submitting) return;
        if (FREEZE_FOR_TESTING) {
            toast.info(`Test mode: would buy ${formatNumber(tier.credits)} credits for ${formatPrice(tier.price)} (no request sent)`);
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await apiClient.post(TOP_UP_CHECKOUT_ENDPOINT, { credits: tier.credits });
            if (data?.checkout_url) {
                // Hand off to Stripe's hosted checkout; it redirects back to
                // /payment/success or /payment/cancel.
                window.location.href = data.checkout_url;
                return;
            }
            toast.error(data?.message || "Could not start checkout. Please try again.");
        } catch (e: any) {
            toast.error(
                e?.response?.data?.detail ||
                e?.response?.data?.message ||
                "Could not start checkout. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    // Rendered on <body>, so it is always centred on the screen - a parent with
    // a CSS transform (the Pricing page has one) would otherwise trap the
    // fixed overlay inside that parent.
    return createPortal(
        <div className="rca-overlay tuc-overlay" onClick={close}>
            <div
                className="rca-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="top-up-credits-title"
            >
                <div className="rca-header">
                    <h2 id="top-up-credits-title">Get top-up credits</h2>
                    <button type="button" className="rca-close" aria-label="Close" onClick={close} disabled={submitting}>
                        <img src="/images/v-close.svg" alt="" width={30} height={30} />
                    </button>
                </div>

                <div className="tuc-card">
                    <div className="tuc-summary">
                        <div>
                            <p className="tuc-price">
                                {formatPrice(tier.price)} <span>one-time</span>
                            </p>
                            <p className="tuc-per-credit">~${perCredit.toFixed(5)} per credit</p>
                        </div>
                        <div className="tuc-credits">
                            <strong>{formatNumber(tier.credits)}</strong>
                            <span>Verification Credits</span>
                        </div>
                    </div>

                    <div
                        className="tuc-slider"
                        style={{ "--tuc-progress": tierIndex / lastIndex } as React.CSSProperties}
                    >
                        <div className="tuc-track" aria-hidden="true">
                            <div className="tuc-track-fill" />
                            <div className="tuc-thumb" />
                        </div>
                        <input
                            type="range"
                            min={0}
                            max={lastIndex}
                            step={1}
                            value={tierIndex}
                            onChange={(e) => setTierIndex(Number(e.target.value))}
                            disabled={submitting}
                            aria-label="Number of credits"
                            aria-valuetext={`${formatNumber(tier.credits)} credits, ${formatPrice(tier.price)}`}
                        />
                        <div className="tuc-ticks" aria-hidden="true">
                            {tiers.map((t, index) => (
                                <button
                                    key={t.credits}
                                    type="button"
                                    tabIndex={-1}
                                    className={index === 0 ? "first" : index === lastIndex ? "last" : ""}
                                    style={{ "--tuc-tick": index / lastIndex } as React.CSSProperties}
                                    onClick={() => setTierIndex(index)}
                                    disabled={submitting}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {blockedReason && (
                    <p className="tuc-footnote" role="alert">{blockedReason}</p>
                )}

                <button
                    type="button"
                    className="rca-submit tuc-buy"
                    onClick={buy}
                    disabled={submitting || optionsLoading || !!blockedReason}
                >
                    {submitting ? (
                        <span className="rca-spinner" />
                    ) : (
                        `Buy ${formatNumber(tier.credits)} Credits - ${formatPrice(tier.price)}`
                    )}
                </button>

                <p className="tuc-footnote">
                    {twelveMonthValidity
                        ? `Top-up credits are valid for 12 months${renewalDate ? `, until ${formatDate(renewalDate)}` : ""}, and stack with any you already have.`
                        : renewalDate
                            ? `Top-ups expire when your plan renews on ${formatDate(renewalDate)} and stack with any you already have.`
                            : "Top-ups expire when your plan renews and stack with any you already have."}
                </p>
            </div>
        </div>,
        document.body
    );
};

export default TopUpCreditsModal;
